import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import PageHeader from 'Components/PageHeader';
import { Upload } from 'antd';
import { WarningOutlined, DownloadOutlined, UploadOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SbTabs, SbButton, SbSelect, SbNotification, SbTooltip, SbInfoTooltip } from 'uiCore';
import { AuthContext } from 'State/AuthContext';
import { downloadCompensationTemplate, bulkUploadCompensation } from 'Api/apiService';
import { COMP_TABS, COMP_TAB_KEYS, COMP_MESSAGES } from 'constants/compensation.constants';
import useCompensationManagement from './useCompensationManagement';
import CompensationTable from './CompensationTable';
import CurrencySettingsTable from './CurrencySettingsTable';
import UploadResultBadge from './UploadResultBadge';
import {
  PageContainer,
  ContentSection,
  TabContentWrapper,
  WarningBannerStyled,
  WarningIcon,
  WarningText,
  BulkToolbarRow,
  PremiumButtonWrapper,
  CompSearchWrapperStyled,
} from './CompensationManagement.styles';

// Column filters the template-download backend understands: it matches each key
// against its own row fields (row.get(key) in values). Other table columns are
// filterable in the UI only — sending them would hide every row in the Excel.
const DOWNLOAD_FILTER_KEYS = [
  'job_code', 'job_title', 'specialty', 'jd_status', 'function',
  'legal_entity', 'job_level', 'location', 'compensation_source',
];
const pickDownloadFilters = (filters = {}) =>
  Object.fromEntries(
    DOWNLOAD_FILTER_KEYS.filter((key) => filters[key]?.length).map((key) => [key, filters[key]])
  );

const TOOLTIP_DEFAULT_CURRENCY = "Set the default currency for all compensation values in your organisation.";
const TOOLTIP_DOWNLOAD_TEMPLATE = "Download the compensation Excel template to fill in bulk compensation data for multiple job codes.";
const TOOLTIP_UPLOAD_TEMPLATE = "Upload a completed compensation template to update multiple job codes at once.";
const TOOLTIP_SEARCH = 'Search by job code, job title, function, or legal entity.';
const SEARCH_FIELDS = ['job_code', 'job_title', 'job_title_for_jd', 'function', 'legal_entity'];

/**
 * CompensationManagement — full-page variant.
 *
 * Renders compensation tables inside a card layout matching the
 * PeoplePlusAIV2 dashboard pattern. Back button navigates to Admin Setup.
 */
const CompensationManagement = () => {
  const navigate = useNavigate();
  const { email: contextEmail } = useContext(AuthContext);
  const email = contextEmail || localStorage.getItem('username') || '';

  const [activeTab, setActiveTab] = useState(COMP_TAB_KEYS.COMP_POSITION);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    allData,
    loading,
    missingCount,
    tableFilters,
    handleTableChange,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditChange,
    handleEditSave,
    handleEditCancel,
    fetchData,
    updatedJobIds,
    takeSnapshot,
    clearUpdatedJobIds,
  } = useCompensationManagement(email);

  useEffect(() => {
    if (email) fetchData();
  }, [email, fetchData]);

  const searchOptions = useMemo(() => {
    const unique = new Set();
    allData.forEach((row) =>
      SEARCH_FIELDS.forEach((f) => {
        const v = row[f];
        if (v && typeof v === 'string' && v.trim()) unique.add(v);
      }),
    );
    return Array.from(unique).map((v) => ({ value: v, label: v }));
  }, [allData]);

  const filteredData = useMemo(() => {
    const term = (searchTerm ?? '').trim().toLowerCase();
    if (!term) return allData;
    return allData.filter((row) =>
      SEARCH_FIELDS.some((f) => row[f]?.toString().toLowerCase().includes(term)),
    );
  }, [allData, searchTerm]);

  // Checked on full data so search doesn't toggle the Specialty column
  const hasSpecialty = useMemo(
    () => allData.some((row) => row?.specialty),
    [allData],
  );

  // ── Download template ────────────────────────────────────────────────────
  const handleDownloadTemplate = useCallback(async () => {
    if (!email) return;
    setDownloading(true);
    try {
      const response = await downloadCompensationTemplate(email, { search: searchTerm, filters: pickDownloadFilters(tableFilters) });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'compensation_template.xlsx';

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      SbNotification.success('Template downloaded', 'Fill in the compensation and total_positions columns, then upload.');
    } catch {
      SbNotification.error('Download failed', 'Unable to download compensation template. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [email, searchTerm, tableFilters]);

  // ── Bulk upload ──────────────────────────────────────────────────────────
  const handleUpload = useCallback(async (file) => {
    if (!email || !file) return false;
    setUploading(true);
    setUploadResult(null);
    takeSnapshot();
    try {
      const result = await bulkUploadCompensation(email, file);
      setUploadResult(result);
      if (result?.failed > 0) {
        SbNotification.warning(
          'Upload completed with errors',
          `${result.successful}/${result.total_rows} rows succeeded. ${result.failed} failed.`,
        );
      } else {
        SbNotification.success(
          'Upload successful',
          `All ${result.successful || result.total_rows} records imported successfully.`,
        );
      }
      // Small delay to allow backend to propagate uploaded changes before re-fetching
      setTimeout(() => fetchData(), 1500);
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'Unable to process the uploaded file.';
      SbNotification.error('Upload failed', errMsg);
    } finally {
      setUploading(false);
    }
    return false;
  }, [email, fetchData, takeSnapshot]);

  // ── Dismiss upload highlights ────────────────────────────────────────────
  const handleDismissUpload = useCallback(() => {
    setUploadResult(null);
    clearUpdatedJobIds();
  }, [clearUpdatedJobIds]);

  // ── Tab content ──────────────────────────────────────────────────────────
  const renderTabContent = (tabKey) => {
    if (tabKey === COMP_TAB_KEYS.COMP_POSITION) {
      return (
        <TabContentWrapper>
          {missingCount > 0 && (
            <WarningBannerStyled>
              <WarningIcon><WarningOutlined /></WarningIcon>
              <WarningText>
                {missingCount} {COMP_MESSAGES.MISSING_WARNING_PREFIX}
              </WarningText>
            </WarningBannerStyled>
          )}
          <CompensationTable
            dataSource={filteredData}
            hasSpecialty={hasSpecialty}
            loading={loading}
            tableFilters={tableFilters}
            onTableChange={handleTableChange}
            editingRowId={editingRowId}
            editValues={editValues}
            onEditStart={handleEditStart}
            onEditChange={handleEditChange}
            onEditSave={handleEditSave}
            onEditCancel={handleEditCancel}
            savingRowId={savingRowId}
            updatedJobIds={updatedJobIds}
          />
        </TabContentWrapper>
      );
    }
    if (tabKey === COMP_TAB_KEYS.CURRENCY_SETTINGS) {
      return <CurrencySettingsTable />;
    }
    return null;
  };

  const tabItems = COMP_TABS.map((tab) => ({
    key: tab.key,
    label: tab.key === COMP_TAB_KEYS.CURRENCY_SETTINGS ? (
      <SbInfoTooltip content={TOOLTIP_DEFAULT_CURRENCY}>
        {tab.label}
      </SbInfoTooltip>
    ) : tab.label,
    disabled: tab.disabled,
    children: renderTabContent(tab.key),
  }));

  return (
    <PageContainer>
      <PageHeader
        title="Compensation Management"
        subtitle="Manage compensation data, FX rates, and currency settings across legal entities"
        onBack={() => navigate('/admin-setup')}
        backLabel="Back to Admin Setup"
        actions={
          <BulkToolbarRow>
            <SbTooltip content={TOOLTIP_SEARCH}>
              <CompSearchWrapperStyled>
                <SbSelect
                  options={searchOptions}
                  value={searchTerm || undefined}
                  suffixIcon={<SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />}
                  onChange={(v) => setSearchTerm(v ?? '')}
                  placeholder="Search job code, title, function…"
                  allowClear
                  showSearch
                  notFoundContent="No matching results"
                  listHeight={300}
                />
              </CompSearchWrapperStyled>
            </SbTooltip>
            <SbTooltip content={TOOLTIP_DOWNLOAD_TEMPLATE}>
              <PremiumButtonWrapper>
                <SbButton
                  buttonType="secondary"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                  loading={downloading}
                >
                  Download Template
                </SbButton>
              </PremiumButtonWrapper>
            </SbTooltip>
            <Upload
              accept=".csv,.xlsx,.xls"
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={uploading}
            >
              <SbTooltip content={TOOLTIP_UPLOAD_TEMPLATE}>
                <PremiumButtonWrapper>
                  <SbButton
                    buttonType="primary"
                    size="small"
                    icon={<UploadOutlined />}
                    loading={uploading}
                  >
                    Upload Compensation
                  </SbButton>
                </PremiumButtonWrapper>
              </SbTooltip>
            </Upload>
          </BulkToolbarRow>
        }
      />

      <ContentSection>
        <SbTabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarExtraContent={
            <UploadResultBadge
              uploadResult={uploadResult}
              onDismiss={handleDismissUpload}
            />
          }
        />
      </ContentSection>
    </PageContainer>
  );
};

export default CompensationManagement;
